import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

const areArraysShallowEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

export const useTagPickerState = ({ initialSelectedIds = [], onApply, mobileMode }) => {
    const [open, setOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(initialSelectedIds || []);
    const [tempSelectedIds, setTempSelectedIds] = useState(initialSelectedIds || []);
    const containerRef = useRef(null);
    const popupRef = useRef(null);

    useEffect(() => {
        const current = initialSelectedIds || [];
        if (!areArraysShallowEqual(current, selectedIds)) {
            setSelectedIds(current);
            setTempSelectedIds(current);
        }
    }, [initialSelectedIds, selectedIds]);

    const handleTagToggle = useCallback(tagId => {
        setTempSelectedIds(prev => (prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]));
    }, []);

    const handleApply = useCallback(() => {
        onApply?.(tempSelectedIds);
        setSelectedIds(tempSelectedIds);
        setOpen(false);
    }, [onApply, tempSelectedIds]);

    const handleClickOutside = useCallback(
        event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setTempSelectedIds(selectedIds);
                setOpen(false);
            }
        },
        [selectedIds],
    );

    const handleReposition = useCallback(() => {
        if (!open || !containerRef.current || !popupRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const popupWidth = popupRef.current.offsetWidth;
        const viewportWidth = window.innerWidth;

        const isOverflowing = containerRect.left + popupWidth > viewportWidth;

        if (isOverflowing) {
            popupRef.current.style.left = 'auto';
            popupRef.current.style.right = '0px';
        } else {
            popupRef.current.style.left = '0px';
            popupRef.current.style.right = 'auto';
        }
    }, [open]);

    useLayoutEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('resize', handleReposition);
            handleReposition();
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleReposition);
        };
    }, [open, handleClickOutside, mobileMode, handleReposition]);

    return {
        open,
        setOpen,
        selectedIds,
        setSelectedIds,
        tempSelectedIds,
        setTempSelectedIds,
        handleTagToggle,
        handleApply,
        containerRef,
        popupRef,
    };
};
